import { Button, Flex, HStack, Stack, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  useReopenNegotiationRoundMutation,
  type NegotiationRoundDto,
} from "../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import {
  isTiptapContentEmpty,
  TipTap,
} from "../../../common/input/TipTap/TipTap";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { useHasPassed } from "../bidDeadlineWindow";
import { roundStillOpen } from "../rounds/roundStillOpen";
import { CompleteRoundButton } from "./CompleteRoundButton";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { RoundInvitesList } from "./RoundInvitesList";

interface Props {
  round: NegotiationRoundDto;
  eventStatus: SourcingEventStatus;
}

export const RoundSummary = ({ round, eventStatus }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const displayDateWithTime = useDisplayDateWithTime();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();
  const { rejectedOrganizationIds } = useRoundSelection();

  const [reopenRound, { isLoading: isReopening }] =
    useReopenNegotiationRoundMutation();

  useHasPassed(round.deadline);
  const isDraft = round.status === NegotiationRoundStatus.Draft;
  const isPublished = round.status === NegotiationRoundStatus.Published;
  const isCompleted = round.status === NegotiationRoundStatus.Completed;
  const canEditRound =
    eventStatus === SourcingEventStatus.Published ||
    eventStatus === SourcingEventStatus.Evaluation;
  const canSettleRound =
    canEditRound || eventStatus === SourcingEventStatus.Evaluated;

  const handleReopen = () => {
    confirmationModal.show({
      title: t("Reopen negotiation round?"),
      description: t(
        "Suppliers will again be able to submit bids for this round.",
      ),
      confirmButtonText: t("Reopen") ?? "Reopen",
      onConfirmed: async () => {
        try {
          await reopenRound({
            eventId: round.eventId,
            roundId: round.id,
          }).unwrap();
          toast({ title: t("Negotiation round reopened"), status: "success" });
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <Stack spacing="5" pt="4">
      {confirmationModal.modal}

      <Stack spacing="2">
        {round.title && <Text fontWeight="medium">{round.title}</Text>}
        <HStack spacing="3">
          <Text fontSize="sm" color="smMuted">
            {t("Deadline")}:{" "}
            {round.deadline ? displayDateWithTime(round.deadline) : "–"}
          </Text>
          {round.isFinalRound && (
            <Text fontSize="sm" color="smMuted">
              {t("Final Round")}
            </Text>
          )}
        </HStack>
        {round.description && !isTiptapContentEmpty(round.description) && (
          <TipTap
            content={round.description}
            editable={false}
            hasBorder={false}
            padding="0"
          />
        )}
      </Stack>

      {round.documents.length > 0 && (
        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium" color="smText">
            {t("Documents")} ({round.documents.length})
          </Text>
          <DocumentsAsOwnerTable documents={round.documents} />
        </Stack>
      )}

      <Stack spacing="2">
        <Text fontSize="sm" fontWeight="medium" color="smText">
          {t("Invited organizations")} ({round.invites.length})
        </Text>
        <RoundInvitesList
          invites={round.invites}
          rejectedOrganizationIds={rejectedOrganizationIds}
        />
      </Stack>

      {round.internalConversation.length > 0 && (
        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium" color="smMuted">
            {t("Internal conversation")} ({round.internalConversation.length})
          </Text>
          <Stack spacing="2">
            {round.internalConversation.map((msg) => (
              <Text
                key={msg.id}
                fontSize="sm"
                color="smMuted"
                bg="smBackgroundSecondary"
                p="2"
                rounded="md"
              >
                {msg.message}
              </Text>
            ))}
          </Stack>
        </Stack>
      )}

      <Flex gap="3" pt="2" flexWrap="wrap">
        {canEditRound && (isDraft || isPublished) && (
          <Button
            colorScheme="teal"
            variant={isDraft ? "solid" : "outline"}
            size="sm"
            leftIcon={<FiEdit2 />}
            onClick={() =>
              navigate(
                urls.app.sourcingEvents.editNegotiationRound.go(
                  round.eventId,
                  round.id,
                ),
              )
            }
          >
            {t("Edit round")}
          </Button>
        )}
        {canSettleRound && isPublished && !roundStillOpen(round, rejectedOrganizationIds) && (
          <CompleteRoundButton round={round} />
        )}
        {canEditRound && isCompleted && (
          <Button
            colorScheme="teal"
            variant="outline"
            size="sm"
            onClick={handleReopen}
            isLoading={isReopening}
          >
            {t("Reopen round")}
          </Button>
        )}
      </Flex>
    </Stack>
  );
};
